import { SignInUrlBuilder } from '@tableau/shared-urls/lib/src/SignInUrlBuilder';

import { Messages } from '~src/codegen/Localize';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { IServerUser } from '~src/userManagement/users/serverApi/types/IServerUser';
import { ISiteUser } from '~src/userManagement/users/serverApi/types/ISiteUser';
import { IUser } from '~src/userManagement/users/serverApi/types/IUser';

import { showCreateActiveDirectoryUserIdentityDialog } from '../components/identityPools/createUserIdentity/CreateActiveDirectoryUserIdentityAction';
import CreateLocalUserIdentityAction from '../components/identityPools/createUserIdentity/CreateLocalUserIdentityAction';
import { callAddToIdentityPoolOnSiteRequest } from '../components/identityPools/requests/AddToIdentityPoolOnSiteUsersRequest';
import { callAddToIdentityPoolRequest } from '../components/identityPools/requests/AddToIdentityPoolRequest';
import { GetDefaultIdentityPoolDescriptionRequest } from '../components/identityPools/requests/GetDefaultIdentityPoolDescriptionRequest';
import { callRemoveFromIdentityPoolOnSiteRequest } from '../components/identityPools/requests/RemoveFromIdentityPoolOnSiteUsersRequest';
import { callRemoveFromIdentityPoolRequest } from '../components/identityPools/requests/RemoveFromIdentityPoolRequest';
import { IListIdentityPoolsResult, listIdentityPools } from '../serverApi/requests/identityPools/ListIdentityPoolsRequest';
import { IIdentityPool } from '../serverApi/types/identityPools/IIdentityPool';


export namespace IdentityPoolService {

  export const LOGIN_PATH_PREFIX = '/authn-service/authenticate/';
  export const DEFAULT_POOL_ID = 'DEFAULT';
  // For the moment, we want to make a best-effort approach, if we fail to fetch the Identity Pools, we make keep the legacy experience working as it was.
  // That way, we avoid blocking any legacy scenarios and avoid impact on setups that don't have identity pools if it fails.
  const SILENCE_FAILURES = true;
  const USER_IDENTITY_NOT_FOUND_MSG = 'Cannot find user identity';

  export function kickoffLogin(identityPool: IIdentityPool, signinQueryParams?: SignInUrlBuilder.QueryParams): void {
    let url = LOGIN_PATH_PREFIX + identityPool.id;


    // Check for redirect OR externalRedirect (mobile/embedded flow uses externalRedirect)
    const redirectPath = signinQueryParams?.externalRedirect || signinQueryParams?.redirect;

    if (redirectPath) {
      url += '?path=' + encodeURIComponent(redirectPath);
    }

    WindowLocationService.navigateToWithoutAddingToHistory(url);
  }

  let cachedIdentityPools: IIdentityPool[] = [];
  let lastPoolFetch: Date = null;

  export function latestIdentityPools(includeDefaultPool: boolean): IIdentityPool[] {
    triggerRefreshCachedPools();
    return includeDefaultPool ? cachedIdentityPools.concat(defaultIdentityPool()) : cachedIdentityPools;
  }

  export function hasIdentityPools(): boolean {
    triggerRefreshCachedPools();
    return cachedIdentityPools.length > 0;
  }

  function triggerRefreshCachedPools() {
    if (!lastPoolFetch || (Date.now() - lastPoolFetch.getTime()) > 5*60*1000) {
      tryFetchActiveIdentityPools();
    }
  }

  export function tryFetchActiveIdentityPools(): Promise<IIdentityPool[]> {
    lastPoolFetch = new Date();
    return listIdentityPools().then((result: IListIdentityPoolsResult) => {
      if (result.errors || !result.identityPools) {
        // Failed to fetch identity pools.
        if (!SILENCE_FAILURES) {
          ToasterService.getInstance().pop({
            type: ToastType.Error,
            text: Messages.unexpectedError(),
          });
        }
        return [];
      } else {
        cachedIdentityPools = result.identityPools.filter(x => x.enabled);
        return cachedIdentityPools;
      }
    });
  }

  export function defaultIdentityPool(): IIdentityPool {
    const storeType = ServerSettingsUnauthenticated.isLdapStore() ? 'LDAP' :
      ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory() ? 'ACTIVE_DIRECTORY' : 'LOCAL';
    return {
      id: DEFAULT_POOL_ID,
      identityStoreInstanceId: 1,
      name: Messages.identityPools_DefaultIdentityPoolName(),
      enabled: true,
      identityStoreType: storeType,
      authType: ServerSettingsUnauthenticated.getAuthenticationType()
    };
  }

  export function getDefaultIdentityPoolDescription(): Promise<IResult> {
    return WebHttpClient.sendRequest(new GetDefaultIdentityPoolDescriptionRequest());
  }

  export function addToIdentityPool(serverUser: IServerUser, identityPool: IIdentityPool): Promise<IResult> {
    const identityUuid = findIdentityUuidInStoreInstance(serverUser, identityPool.identityStoreInstanceId);
    if (identityUuid === undefined) {
      return createUserIdentity(identityPool, serverUser, true);
    }

    return callAddToIdentityPoolRequest(serverUser, identityUuid, identityPool.id)
      .then((result: IResult) => {
        const message = Messages.identityPools_AddToPoolAction_SuccessfullyAddUserToPool({
          USER_DISPLAY_NAME: serverUser.displayName,
          IDENTITY_POOL_NAME: identityPool.name
        });
        ToasterService.instance.pop({ type: ToastType.Success, text: message });
        return result;
      });
  }

  export function addToIdentityPoolOnSiteUser(siteUser: ISiteUser, identityPool: IIdentityPool): Promise<IResult> {
    const identityUuid = findIdentityUuidInStoreInstance(siteUser, identityPool.identityStoreInstanceId);
    if (identityUuid === undefined) {
      return createUserIdentity(identityPool, siteUser, false);
    }

    return callAddToIdentityPoolOnSiteRequest(siteUser, identityUuid, identityPool.id)
      .then((result: IResult) => {
        const message = Messages.identityPools_AddToPoolAction_SuccessfullyAddUserToPool({
          USER_DISPLAY_NAME: siteUser.displayName,
          IDENTITY_POOL_NAME: identityPool.name
        });
        ToasterService.instance.pop({ type: ToastType.Success, text: message });
        return result;
      });
  }

  export function removeFromIdentityPool(serverUser: IServerUser, identityPool: IIdentityPool): Promise<IResult> {
    return callRemoveFromIdentityPoolRequest(serverUser.id, identityPool.id)
      .then((result: IResult) => {
        const msg = Messages.identityPools_RemoveFromPoolAction_SuccessfullyRemoveUserFromPool({
          USER_DISPLAY_NAME: serverUser.displayName,
          IDENTITY_POOL_NAME: identityPool.name
        });
        ToasterService.instance.pop({ type: ToastType.Success, text: msg });

        return result;
      });
  }

  export function removeFromIdentityPoolOnSiteUser(siteUser: ISiteUser, identityPool: IIdentityPool): Promise<IResult> {
    return callRemoveFromIdentityPoolOnSiteRequest(siteUser.luid, identityPool.id)
      .then((result: IResult) => {
        const msg = Messages.identityPools_RemoveFromPoolAction_SuccessfullyRemoveUserFromPool({
          USER_DISPLAY_NAME: siteUser.displayName,
          IDENTITY_POOL_NAME:identityPool.name
        });
        ToasterService.instance.pop({ type: ToastType.Success, text: msg });

        return result;
      });
  }

  function findIdentityUuidInStoreInstance(user: IUser, targetStoreId: number): string | undefined {
    if (user.poolMemberships === undefined || user.poolMemberships === null) {
      return undefined;
    }

    const poolMembership = user.poolMemberships.find(x => x.identityPool.identityStoreInstanceId === targetStoreId);
    return poolMembership === undefined ? undefined : poolMembership.identityUuid;
  }

  function createUserIdentity(identityPool: IIdentityPool, user: IUser, isServerUser: boolean): Promise<any> {
    if (identityPool.identityStoreType === 'LOCAL') {
      return CreateLocalUserIdentityAction.showCreateLocalUserIdentityDialog(identityPool, user, isServerUser);
    }

    return showCreateActiveDirectoryUserIdentityDialog(identityPool, user, isServerUser);
  }
}
